import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { Grid, TextField } from "@material-ui/core";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { WorkOrderPart } from "../../../../models/WorkOrderPart";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
interface Props {
	workOrderParts: WorkOrderPart[];
	onSelectionChanged: (selectedParts: WorkOrderPart[]) => void;
}

export const AssetPartsTable = ({
	workOrderParts,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		state: { assetParts },
	} = useWorkOrderModalsContext();

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "PartName", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return assetParts.map((part, index) => ({
			...part,
			id: `${part.PartName}-${index}`,
			isSelected: workOrderParts.some(
				(wd) => wd.PartName === part.PartName && wd.IsAssetResource,
			),
		}));
	}, [assetParts, workOrderParts]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 100,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "PartName",
			headerName: "Part Name",
			flex: 3,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.PartName?.localeCompare(nodeB.data.PartName);
				}
			},
		},
		{
			field: "Description",
			headerName: "Description",
			flex: 9,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.Description?.localeCompare(nodeB.data.Description);
				}
			},
		},
	];

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.isSelected) {
				node.setSelected(true);
			}
		});
		sortRows();
	}, [gridApi, workOrderParts]);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid item xs={12}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid>
			</Grid>
			<div
				className="ag-theme-alpine"
				style={{ width: "100%", height: "45vh" }}
			>
				<AgGridReact
					getRowId={(params) => params.data.id}
					rowHeight={30}
					headerHeight={25}
					animateRows={true}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={globalDefaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={loadingOverlayComponent}
					rowSelection="multiple"
					rowMultiSelectWithClick
					onSelectionChanged={onAgGridSelectionChanged}
					suppressContextMenu={true}
					loading={isLoading}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
